import * as T from './types';

/**
 * Opens the Resume Session Modal MFE through Modal Northbound API and resolves
 * the user's selection. Modal Northbound API is only needed for this rare,
 * late-triggered flow, so it's resolved lazily (matches GraphQLService /
 * VirtualNavigationHistory), and a pending open is reused so a second call for
 * the same key can't stack a duplicate overlay (Modal Northbound API itself
 * has no dedup protection).
 */
export default class ResumeSessionModalLauncher {
  private readonly _key: string;
  private _pendingSelection?: Promise<T.ResumeSessionModalOutput>;

  constructor(key: string) {
    this._key = key;
  }

  open(input: T.ResumeSessionModalInput): Promise<T.ResumeSessionModalOutput> {
    if (this._pendingSelection !== undefined) {
      return this._pendingSelection;
    }

    const selectionPromise = this._openModal(input).finally(() => {
      this._pendingSelection = undefined;
    });

    this._pendingSelection = selectionPromise;
    return selectionPromise;
  }

  private async _openModal({
    session,
    assetReference
  }: T.ResumeSessionModalInput): Promise<T.ResumeSessionModalOutput> {
    const api = await System.import<
      typeof import('@clientos/modal-northbound-api')
    >('@clientos/modal-northbound-api').then((m) => m.default.v1);

    const closeModal = () => void api.close({ key: this._key });

    return new Promise<T.ResumeSessionModalOutput>((resolve, reject) => {
      api
        .call({
          key: this._key,
          assetReference,
          properties: {
            session,
            onResume: () => {
              closeModal();
              resolve({ selection: 'resume' });
            },
            onStartNew: () => {
              closeModal();
              resolve({ selection: 'start-new' });
            }
          }
        })
        // call() already inserts the modal's backdrop before it can fail to
        // load/render, so it must be explicitly closed or it's left stuck on screen.
        .catch((error) => {
          closeModal();
          reject(error);
        });
    });
  }
}
